import { CCInteger, Component, _decorator } from 'cc';
import { DEV } from 'cc/env';

enum EventType {
    ELEMENT_INITIALIZED = 'ELEMENT_INITIALIZED',
}

const { ccclass, property } = _decorator;

/**
 * Abstract base class that adapts DOM elements to work within Cocos Creator's component system.
 *
 * This adapter bridges the gap between Cocos Creator's component lifecycle and DOM element management,
 * providing automatic initialization, cleanup, and property synchronization.
 *
 * @template T - The type of HTML element this adapter manages
 */
@ccclass('DOMElementAdapter')
export default abstract class DOMElementAdapter<T extends HTMLElement> extends Component {
    public static readonly EVENT_TYPE = EventType;

    @property({ type: CCInteger, tooltip: DEV && 'zIndex of the element' })
    public readonly renderOrder: number = 0;

    protected _element: T = null;
    private _initialized: boolean = false;

    public get element() {
        if (!this._element) {
            this.initElement();
        }
        return this._element;
    }

    public get initialized() {
        return this._initialized;
    }

    protected onLoad() {
        this.initElement();
    }

    protected onDestroy() {
        this.removeElement();
    }

    protected initElement() {
        if (this._element) {
            return;
        }
        this._element = this.findElement();
        if (this._element) {
            this._initialized = true;
            this.applyPropertyOnElement(this._element);
            this.emit(EventType.ELEMENT_INITIALIZED, this._element);
        }
    }

    protected removeElement() {
        if (!this._element) {
            return;
        }
        this._element.remove();
        this._element = null;
    }

    protected applyPropertyOnElement(element: T) {
        element.style.zIndex = this.renderOrder.toString();
    }

    protected abstract findElement(): T;
}
